"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import { useInstantNav } from "@/hooks/useInstantNav"
import {
  FileText,
  Lock,
  LogOut,
  ShoppingBag,
  User,
} from "lucide-react";

import { logout } from "@/lib/api/business-api";
import { clearAuthData, getAuthToken, getRefreshToken } from "@/lib/auth-utils";
import { usePageTranslation } from "@/hooks/usePageTranslation";
import { useAuthStore } from "@/store/auth";

import { AddressesView } from "./AddressesView";
import { AddCredentialsCard } from "./AddCredentialsCard";
import { ChangePasswordForm } from "./ChangePasswordForm";
import { EditProfileForm } from "./EditProfileForm";
import { OrderDetails } from "./OrderDetails";
import { OrdersList } from "./OrdersList";
import { UserProfileView } from "./UserProfileView";
import { WishlistView } from "./WishlistView";

interface UserAccountDashboardProps {
  businessId: string;
}

type SectionType =
  | "dashboard"
  | "orders"
  | "order-details"
  | "profile"
  | "edit-profile"
  | "change-password"
  | "addresses"
  | "wishlist";

interface AccountCard {
  id: SectionType;
  label: string;
  icon: any;
  description: string;
}

const headingFontStyle = { fontFamily: "var(--brand-heading-font, inherit)" };

export function UserAccountDashboard({
  businessId, // Reserved for future API calls (orders, addresses, etc.)
}: UserAccountDashboardProps) {
  const { t } = usePageTranslation();
  const navigate = useInstantNav()
  const searchParams = useSearchParams();
  const authLogout = useAuthStore((state) => state.logout);

  const [activeSection, setActiveSection] = useState<SectionType>("dashboard");
  const [userData, setUserData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  // Initialize active section from URL on mount
  useEffect(() => {
    const section = searchParams.get("section") as SectionType;
    const orderId = searchParams.get("orderId");

    if (
      section &&
      [
        "dashboard",
        "orders",
        "order-details",
        "profile",
        "edit-profile",
        "change-password",
        "addresses",
        "wishlist",
      ].includes(section)
    ) {
      setActiveSection(section);
      if (orderId) {
        setSelectedOrderId(orderId);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    // Load user data from both localStorage and sessionStorage
    const storedUserDataLocal = localStorage.getItem("user_data");
    const storedUserDataSession = sessionStorage.getItem("user_data");
    const storedUserData = storedUserDataLocal || storedUserDataSession;

    if (storedUserData) {
      try {
        setUserData(JSON.parse(storedUserData));
        console.log("✅ User data loaded in dashboard");
      } catch (error) {
        console.error("❌ Failed to parse user data:", error);
      }
    } else {
      console.warn("⚠️ No user data found in storage");
    }

    setIsLoading(false);
  }, []);

  // Helper function to navigate to a section and update URL
  const navigateToSection = (section: SectionType, orderId?: string) => {
    setActiveSection(section);

    // Build query params
    const params = new URLSearchParams();
    if (section !== "dashboard") {
      params.set("section", section);
    }
    if (orderId) {
      params.set("orderId", orderId);
    }

    // Update URL
    const queryString = params.toString();
    const newUrl = queryString ? `/account?${queryString}` : "/account";
    navigate(newUrl);
  };

  const handleLogout = async () => {
    try {
      // Get tokens from storage using auth-utils
      const accessToken = getAuthToken();
      const refreshToken = getRefreshToken();

      if (!accessToken || !refreshToken) {
        console.warn("⚠️ No tokens found, clearing storage and redirecting");
        clearAuthData();
        authLogout(); // Clear Zustand auth store
        window.location.href = "/login";
        return;
      }

      console.log("🌐 Calling logout API...");

      // Call logout API
      await logout(accessToken, refreshToken);

      console.log("✅ Logout API call successful");

      // Clear all auth data from both storages
      clearAuthData();

      // Clear Zustand auth store state
      authLogout();

      // Redirect to login
      window.location.href = "/login";
    } catch (error: any) {
      console.error("❌ Logout API error:", error);
      // Even if API fails, clear local data and redirect
      clearAuthData();
      authLogout(); // Clear Zustand auth store
      window.location.href = "/login";
    }
  };

  const handleProfileUpdate = (updatedUser: any) => {
    // Update user data in state
    setUserData(updatedUser);

    // Determine which storage was used (check where current data exists)
    const isLocalStorage = !!localStorage.getItem("user_data");
    const isSessionStorage = !!sessionStorage.getItem("user_data");

    // Update the storage that was originally used
    if (isLocalStorage) {
      localStorage.setItem("user_data", JSON.stringify(updatedUser));
      console.log("✅ Profile updated in localStorage");
    } else if (isSessionStorage) {
      sessionStorage.setItem("user_data", JSON.stringify(updatedUser));
      console.log("✅ Profile updated in sessionStorage");
    }

    // Switch back to profile view
    navigateToSection("profile");
  };

  const handleViewOrderDetails = (orderId: string) => {
    setSelectedOrderId(orderId);
    navigateToSection("order-details", orderId);
  };

  const handleBackToOrders = () => {
    setSelectedOrderId(null);
    navigateToSection("orders");
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-brand-primary" />
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="mx-auto max-w-2xl py-12 text-center">
        <div className="rounded-brand-lg border border-brand-primary/30 bg-brand-primary/5 p-8">
          <h2
            className="mb-4 text-2xl font-bold text-brand-fg"
            style={headingFontStyle}
          >
            {t("account.notLoggedIn")}
          </h2>
          <p className="mb-6 text-brand-fg">{t("account.notLoggedInMessage")}</p>
          <Link
            href="/login"
            className="inline-block rounded-brand bg-brand-primary px-6 py-3 font-semibold text-brand-primary-foreground transition-colors hover:bg-brand-primary-hover"
          >
            {t("auth.goToLogin")}
          </Link>
        </div>
      </div>
    );
  }

  const accountCards: AccountCard[] = [
    {
      id: "orders",
      label: t("account.ordersCard"),
      icon: ShoppingBag,
      description: t("account.ordersCardDesc"),
    },
    {
      id: "profile",
      label: t("account.profileCard"),
      icon: User,
      description: t("account.profileCardDesc"),
    },
    {
      id: "change-password",
      label: t("account.changePassword"),
      icon: Lock,
      description: t("account.changePasswordDesc"),
    },
    // Addresses and Wishlist cards hidden - can be enabled when needed
  ];

  // Dashboard View - Show cards
  if (activeSection === "dashboard") {
    return (
      <div className="account-dashboard mx-auto max-w-7xl">
        {/* Header with User Info */}
        <div className="mb-8 rounded-brand-lg border border-brand-border bg-brand-surface p-6 shadow-sm">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between md:gap-8">
            <div className="flex items-center gap-4">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-brand-primary/10">
                <User className="h-10 w-10 text-brand-primary" />
              </div>
              <div>
                <p className="text-sm text-brand-muted">{t("account.hello")}</p>
                <h1
                  className="text-2xl font-bold text-brand-fg md:text-3xl"
                  style={headingFontStyle}
                >
                  {userData.name || userData.email}
                </h1>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 rounded-brand border border-brand-danger/40 px-4 py-2 text-brand-danger transition-colors hover:bg-brand-danger/10"
            >
              <LogOut className="h-5 w-5" />
              <span>{t("header.logout")}</span>
            </button>
          </div>
        </div>

        <AddCredentialsCard />

        {/* Account Cards Grid */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {accountCards.map((card) => {
            const Icon = card.icon;
            return (
              <button
                key={card.id}
                onClick={() => navigateToSection(card.id)}
                className="account-card group rounded-brand-lg border border-brand-border bg-brand-surface p-6 text-start shadow-sm transition-all hover:shadow-lg"
              >
                <div className="flex flex-col items-center justify-center space-y-4">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-primary/10 transition-transform group-hover:scale-110">
                    <Icon className="h-8 w-8 text-brand-primary" />
                  </div>
                  <div className="text-center">
                    <h3 className="text-lg font-bold text-brand-fg">
                      {card.label}
                    </h3>
                    <p className="mt-1 text-sm text-brand-muted">
                      {card.description}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Content View - Show specific section
  return (
    <div className="account-dashboard mx-auto max-w-5xl">
      {/* Back Button */}
      <button
        onClick={() => navigateToSection("dashboard")}
        className="mb-6 flex items-center space-x-2 text-brand-primary transition-colors hover:text-brand-primary-hover"
      >
        <FileText className="h-5 w-5" />
        <span className="font-medium">{t("account.backToDashboard")}</span>
      </button>

      {/* Content Section */}
      <div className="overflow-hidden rounded-brand-lg border border-brand-border bg-brand-surface shadow-sm">
        <div className="p-6">
          {activeSection === "orders" && (
            <OrdersList
              userData={userData}
              businessId={businessId}
              onViewDetails={handleViewOrderDetails}
            />
          )}

          {activeSection === "order-details" && selectedOrderId && (
            <OrderDetails
              orderId={selectedOrderId}
              userData={userData}
              businessId={businessId}
              onBack={handleBackToOrders}
            />
          )}

          {activeSection === "profile" && (
            <UserProfileView
              userData={userData}
              onEditClick={() => navigateToSection("edit-profile")}
            />
          )}

          {activeSection === "edit-profile" && (
            <EditProfileForm
              userData={userData}
              onSuccess={handleProfileUpdate}
              onCancel={() => navigateToSection("profile")}
            />
          )}

          {activeSection === "change-password" && (
            <ChangePasswordForm
              userData={userData}
              onSuccess={() => navigateToSection("dashboard")}
              onCancel={() => navigateToSection("dashboard")}
            />
          )}

          {activeSection === "addresses" && (
            <AddressesView userData={userData} businessId={businessId} />
          )}

          {activeSection === "wishlist" && (
            <WishlistView userData={userData} businessId={businessId} />
          )}
        </div>
      </div>
    </div>
  );
}
